import type { FamilyMemberInfo, FamilyRelationshipDetails, FamilySelectionOption } from "../../hooks/use-families";

const getRelationshipValue = (relationship: FamilyRelationshipDetails): string =>
  `${relationship.relationship?.value ?? ""}`;

const getMemberRelationships = (member: FamilyMemberInfo): FamilyRelationshipDetails[] => {
  if (member.relationships?.length) {
    return member.relationships.filter(
      relationship => relationship.subjectContactId === member.id || relationship.objectContactId === member.id
    );
  }

  return member.subjectRelationships ?? [];
};

const mapRelationshipLabel = (
  relationship: FamilyRelationshipDetails,
  familyRelationshipLookup: Record<string, string>
): string => {
  const relationshipValue = getRelationshipValue(relationship);
  const relationshipName = relationship.relationship?.name ?? "";

  return familyRelationshipLookup[relationshipValue] || relationshipName || relationshipValue;
};

const isEmergencyRelationship = (
  relationship: FamilyRelationshipDetails,
  normalizedEmergencyRelationshipId: string
): boolean =>
  Boolean(normalizedEmergencyRelationshipId) &&
  getRelationshipValue(relationship) === normalizedEmergencyRelationshipId;

const matchesContactPair = (relationship: FamilyRelationshipDetails, leftContactId: number, rightContactId: number) =>
  (relationship.subjectContactId === leftContactId && relationship.objectContactId === rightContactId) ||
  (relationship.subjectContactId === rightContactId && relationship.objectContactId === leftContactId);

export const findPairRelationshipId = ({
  familySelectionList,
  inquirerId,
  studentId,
  emergencyContactRelationshipId,
  selectableRelationshipIds
}: {
  familySelectionList?: FamilySelectionOption[];
  inquirerId?: number;
  studentId?: number;
  emergencyContactRelationshipId?: string;
  selectableRelationshipIds?: string[];
}) => {
  if (!familySelectionList?.length || !inquirerId || !studentId) {
    return undefined;
  }

  const normalizedEmergencyRelationshipId = emergencyContactRelationshipId ? `${emergencyContactRelationshipId}` : "";
  const selectableRelationshipIdSet = selectableRelationshipIds?.length
    ? new Set(selectableRelationshipIds.map(value => `${value}`))
    : undefined;
  let sequence = 0;
  let winner: { rank: number; relationshipValue: string } | undefined;

  for (const family of familySelectionList) {
    for (const member of family.members ?? []) {
      const memberRelationships = getMemberRelationships(member);

      for (const relationship of memberRelationships) {
        sequence += 1;
        const relationshipValue = getRelationshipValue(relationship);

        if (!relationshipValue) {
          continue;
        }
        if (isEmergencyRelationship(relationship, normalizedEmergencyRelationshipId)) {
          continue;
        }
        if (selectableRelationshipIdSet && !selectableRelationshipIdSet.has(relationshipValue)) {
          continue;
        }
        if (!matchesContactPair(relationship, inquirerId, studentId)) {
          continue;
        }

        const relationshipRowId = Number((relationship as { id?: number | string }).id);
        const rank = Number.isFinite(relationshipRowId) ? relationshipRowId : sequence;

        if (!winner || rank >= winner.rank) {
          winner = { rank, relationshipValue };
        }
      }
    }
  }

  return winner?.relationshipValue;
};

export const getMemberRelationshipLabelsToStudent = ({
  member,
  studentId,
  emergencyContactRelationshipId,
  familyRelationshipLookup
}: {
  member: FamilyMemberInfo;
  studentId?: number;
  emergencyContactRelationshipId?: string;
  familyRelationshipLookup: Record<string, string>;
}) => {
  if (!studentId || member.id === studentId) {
    return [];
  }

  const normalizedEmergencyRelationshipId = emergencyContactRelationshipId ? `${emergencyContactRelationshipId}` : "";
  const labels = new Set<string>();
  const memberRelationships = getMemberRelationships(member);

  memberRelationships.forEach(relationship => {
    if (!matchesContactPair(relationship, member.id, studentId)) {
      return;
    }

    if (isEmergencyRelationship(relationship, normalizedEmergencyRelationshipId)) {
      return;
    }

    labels.add(mapRelationshipLabel(relationship, familyRelationshipLookup));
  });

  return Array.from(labels);
};
